import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { services } from "../mock";
import { kyoLogoSvg } from "./Logo";

/* ── Echo rings ── */
function EchoRings({ tone = "black" }) {
  const c = tone === "pink" ? "border-[#ED2E24]/20" : "border-black/10";
  return (
    <>
      <span className={`absolute inset-0 rounded-full border ${c} -translate-x-3 -translate-y-3`} aria-hidden="true" />
      <span className={`absolute inset-0 rounded-full border ${c} -translate-x-1 -translate-y-1`} aria-hidden="true" />
    </>
  );
}

/* ── Service badge ── */
function ServiceBadge({ variant }) {
  const wrap = "relative w-[76px] h-[76px] shrink-0";

  if (variant === "zip") {
    return (
      <div className={wrap}>
        <EchoRings tone="pink" />
        <div className="relative w-[76px] h-[76px] rounded-full bg-white shadow-[0_10px_30px_rgba(0,0,0,0.14)] flex items-center justify-center p-3 overflow-hidden border border-gray-100">
          <span className="flex items-start leading-none">
            <span className="font-extrabold italic text-[#F26722] text-[10px] tracking-tight">ZIP</span>
            <span className="font-semibold text-[#2E2E5E] text-[10px]">Express</span>
            <sup className="text-[5px] text-[#2E2E5E] mt-0.5">TM</sup>
          </span>
        </div>
      </div>
    );
  }

  const bg = { light: "bg-white", red: "bg-[#ED2E24]", navy: "bg-[#1B1B8F]" }[variant];
  const markColor = variant === "light" ? "#ED2E24" : "#ffffff";

  return (
    <div className={wrap}>
      <EchoRings tone={variant === "light" ? "pink" : "black"} />
      <div className={`relative w-[76px] h-[76px] rounded-full shadow-[0_10px_30px_rgba(0,0,0,0.18)] flex items-center justify-center ${bg}`}>
        <span
          className="flex items-start"
          dangerouslySetInnerHTML={{ __html: kyoLogoSvg(markColor, { width: 46, height: 17 }) }}
        />
        <sup className={`text-[6px] mt-1.5 -ml-0.5 ${variant === "light" ? "text-[#ED2E24]" : "text-white"}`}>TM</sup>
      </div>
    </div>
  );
}

/* ── Generic service card (KYo Couriers, Caribbean Logistics, Export) ── */
function ServiceCard({ s, index }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const cardLeft = s.imageSide === "right";

  const Card = (
    <div className="relative bg-white shadow-[0_8px_32px_rgba(0,0,0,0.10)] rounded-xl flex flex-col px-9 pb-9 pt-12 h-full">
      <div className={`absolute top-0 -translate-y-10 ${cardLeft ? "left-9" : "right-9"}`}>
        <ServiceBadge variant={s.badgeVariant} />
      </div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h3 className="text-[24px] lg:text-[26px] font-light text-[#1a1a1a] leading-snug">{s.name}</h3>
        {s.tag && (
          <span className="text-[13px] font-bold tracking-wide text-[#ED2E24]">{s.tag}</span>
        )}
      </div>
      <p className="text-[15px] leading-[1.8] text-[#5f5f5f] flex-1 mb-8">{s.description}</p>
      <div>
        <button
          onClick={() => toast(`${s.name}`, { description: "More details coming soon." })}
          className="bg-[#ED2E24] hover:bg-[#c9231a] text-white text-[14px] font-bold px-8 py-3 rounded-lg transition-colors shadow-sm"
          data-testid={`service-readmore-${s.id}`}
        >
          Read More
        </button>
      </div>
    </div>
  );

  const Photo = (
    <div className="overflow-hidden rounded-xl h-full min-h-[340px] lg:min-h-[400px]">
      <img
        src={s.image}
        alt={s.name}
        className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-105"
      />
    </div>
  );

  return (
    <div
      ref={ref}
      id={s.id}
      className="relative scroll-mt-24"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(44px)",
        transition: `opacity 0.65s ease ${index * 0.15}s, transform 0.65s ease ${index * 0.15}s`,
      }}
    >
      {cardLeft ? (
        <div className="grid lg:grid-cols-2 lg:gap-0 gap-6 items-stretch">
          <div className="relative z-20 lg:-mr-14 flex flex-col h-full pt-12">{Card}</div>
          <div className="relative">{Photo}</div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 lg:gap-0 gap-6 items-stretch">
          <div className="relative">{Photo}</div>
          <div className="relative z-20 lg:-ml-14 flex flex-col h-full pt-12">{Card}</div>
        </div>
      )}
      <div
        className={`hidden lg:block h-[10px] bg-[#ED2E24] ${
          cardLeft ? "w-[calc(50%+3.5rem)] mr-auto" : "w-[calc(50%+3.5rem)] ml-auto"
        }`}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   ZipExpress card — rebuilt from scratch to match screenshot:
   • Light gray outer wrapper, rounded-2xl, subtle shadow
   • Large rounded image on the LEFT (objectPosition left center
     to crop out the embedded UI baked into the right of the photo)
   • White content panel on the RIGHT, side-by-side (not overlapping)
   • ZipExpress circular badge at TOP-RIGHT of content, floating above
   • Red accent bar at the bottom spanning the right column
   ──────────────────────────────────────────────────────────── */
function ZipExpressCard({ s, index }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      id="zip"
      className="relative scroll-mt-24"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(44px)",
        transition: `opacity 0.65s ease ${index * 0.15}s, transform 0.65s ease ${index * 0.15}s`,
      }}
    >
      {/* Light gray outer card — extra top padding gives badge room to float up */}
      <div className="bg-[#f0f0f0] rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.07)] pt-14 px-5 pb-5 lg:pt-16 lg:px-6 lg:pb-6">
        <div className="grid lg:grid-cols-2 gap-4 lg:gap-5 items-stretch">

          {/* ── LEFT: image ── */}
          <div className="overflow-hidden rounded-xl min-h-[300px] lg:min-h-[400px]">
            <img
              src={s.image}
              alt="ZipExpress branded white delivery trucks"
              className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-105"
            />
          </div>

          {/* ── RIGHT: white content panel ── */}
          <div className="relative bg-white rounded-xl shadow-[0_6px_28px_rgba(0,0,0,0.09)] flex flex-col px-9 pb-9 pt-12">

            {/* Badge — absolute, floats above top-right corner of white panel */}
            <div className="absolute top-0 right-9 -translate-y-10">
              <ServiceBadge variant="zip" />
            </div>

            {/* Title + COMING SOON tag */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <h3 className="text-[24px] lg:text-[26px] font-light text-[#1a1a1a] leading-snug">
                {s.name}
              </h3>
              {s.tag && (
                <span className="text-[13px] font-bold tracking-wide text-[#ED2E24]">{s.tag}</span>
              )}
            </div>

            {/* Description */}
            <p className="text-[15px] leading-[1.8] text-[#5f5f5f] flex-1 mb-8">
              {s.description}
            </p>

            {/* CTA */}
            <div>
              <button
                onClick={() => toast(`${s.name}`, { description: "More details coming soon." })}
                className="bg-[#ED2E24] hover:bg-[#c9231a] text-white text-[14px] font-bold px-8 py-3 rounded-lg transition-colors shadow-sm"
                data-testid="service-readmore-zip"
              >
                Read More
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Red accent bar — right half, below card */}
      <div className="hidden lg:block h-[10px] bg-[#ED2E24] w-1/2 ml-auto" />
    </div>
  );
}

/* ── Section wrapper ── */
export default function Services() {
  return (
    <section id="about" className="py-16 lg:py-24 scroll-mt-20 bg-white">
      <div className="max-w-[1240px] mx-auto px-5 lg:px-6">
        <div className="text-center mb-20">
          <h2 className="text-[34px] lg:text-[40px] font-light text-[#1a1a1a]">Our Services</h2>
          <div className="mx-auto mt-2 w-16 h-1 bg-[#ED2E24] rounded-full" />
        </div>
        <div className="flex flex-col gap-24">
          {services.map((s, i) =>
            s.id === "zip" ? (
              <ZipExpressCard key={s.id} s={s} index={i} />
            ) : (
              <ServiceCard key={s.id} s={s} index={i} />
            )
          )}
        </div>
      </div>
    </section>
  );
}
